// Navigation configuration system for sidebar navigation
// This file defines all navigation items with their roles, paths, and icons

/**
 * Navigation item configuration
 * @typedef {Object} NavigationItem
 * @property {string} id - Unique identifier for the navigation item
 * @property {string} label - Display label for the navigation item
 * @property {string} path - Route path for navigation
 * @property {string} icon - Material Design icon name
 * @property {string[]} roles - Array of user roles that can access this item
 * @property {string[]} [requiredPermissions] - Array of permissions required to access this item
 * @property {boolean} [isDefault] - Whether this is the default route for a role
 */

/**
 * Main navigation configuration
 * Defines all available navigation items with their access controls
 */
export const NAVIGATION_CONFIG = [
  // Downline, Recruiting, Training, Performance and Admin are temporarily
  // hidden from the menu; their routes still work.
  {
    id: "home",
    label: "Home",
    path: "/dashboard",
    icon: "home",
    roles: ["MANAGER", "REP", "COMPANY_ADMIN", "REPPI_ADMIN"],
    isDefault: true,
  },
  {
    id: "reps",
    label: "Reps",
    path: "/reps",
    icon: "groups",
    roles: ["MANAGER", "COMPANY_ADMIN"],
  },
  // {
  //   id: "downline-management",
  //   label: "Downline",
  //   path: "/reps/downline",
  //   icon: "device_hub",
  //   roles: ["MANAGER"],
  // },
  // {
  //   id: "recruiting",
  //   label: "Recruiting",
  //   path: "/recruiting",
  //   icon: "person_add",
  //   roles: ["MANAGER"],
  // },
  // {
  //   id: "training",
  //   label: "Training",
  //   path: "/training",
  //   icon: "menu_book",
  //   roles: ["MANAGER", "REP"],
  // },
  // {
  //   id: "performance",
  //   label: "Performance",
  //   path: "/performance",
  //   icon: "whatshot",
  //   roles: ["MANAGER", "REP"],
  // },
  // {
  //   id: "company-users",
  //   label: "Company Downline",
  //   path: "/company-downline",
  //   icon: "supervisor_account",
  //   roles: ["COMPANY_ADMIN"],
  // },
  {
    id: "reppi-admin",
    label: "Reppi Admin",
    path: "/reppi-admin",
    icon: "admin_panel_settings",
    roles: ["REPPI_ADMIN"],
    isDefault: true, // Default for REPPI_ADMIN role
  },
  {
    id: "agora-chat",
    label: "Agora Chat",
    path: "/agora-chat",
    icon: "forum",
    roles: ["REPPI_ADMIN"],
  },
  // {
  //   id: "reports",
  //   label: "Reports",
  //   path: "/reports",
  //   icon: "assessment",
  //   roles: ["COMPANY_ADMIN"],
  // },
  {
    id: "welcome-survey",
    label: "Welcome Survey",
    path: "/welcome-survey",
    icon: "how_to_reg",
    // Company admins only. Visibility is additionally gated by the
    // welcome_survey JWT module in useNavigation.
    roles: ["COMPANY_ADMIN"],
  },
  // {
  //   id: "fieldroutes-setup",
  //   label: "FieldRoutes Setup",
  //   path: "/fieldroutes-setup",
  //   icon: "hub",
  //   roles: ["COMPANY_ADMIN"],
  // },
  // {
  //   id: "organization",
  //   label: "Admin",
  //   path: "/admin-main",
  //   icon: "account_tree",
  //   roles: ["MANAGER", "COMPANY_ADMIN"],
  // },
  // {
  //   id: "org-structure",
  //   label: "Org Structure",
  //   path: "/org-structure",
  //   // Shown only to users whose JWT has leadership_team: true, regardless of
  //   // role. Like every non-welcome-survey item, visibility is additionally
  //   // gated by the general_suite JWT module in useNavigation.
  //   roles: ["MANAGER", "REP", "COMPANY_ADMIN", "REPPI_ADMIN"],
  //   requiresLeadershipTeam: true,
  //   icon: "lan",
  // },
  // {
  //   id: "selling-years",
  //   label: "Selling Years",
  //   path: "/selling-years",
  //   icon: "event_note",
  //   roles: ["COMPANY_ADMIN"],
  // },
  {
    id: "subscription",
    label: "Billing",
    path: "/subscription",
    icon: "credit_card",
    roles: ["COMPANY_ADMIN"],
  },
  {
    id: "company",
    label: "Setup",
    path: "/company",
    icon: "business_center",
    roles: ["COMPANY_ADMIN"],
    isDefault: true, // Default for COMPANY_ADMIN role
  },
  // {
  //   id: "chat",
  //   label: "Chat",
  //   path: "/chat",
  //   icon: "chat_bubble",
  //   roles: ["MANAGER", "REP", "COMPANY_ADMIN"],
  // },
];

/**
 * Filter navigation items based on user role and permissions
 * @param {string} userRole - The user's role (MANAGER, REP, COMPANY_ADMIN)
 * @param {string[]} userPermissions - Array of user permissions (optional)
 * @returns {NavigationItem[]} Array of navigation items accessible to the user
 */
export const getNavigationItemsForRole = (userRole, userPermissions = []) => {
  if (!userRole) {
    return [];
  }

  return NAVIGATION_CONFIG.filter(item => {
    // Check if user has the required role
    if (!item.roles.includes(userRole)) {
      return false;
    }

    // If item has required permissions, check if user has them
    if (item.requiredPermissions && item.requiredPermissions.length > 0) {
      // User must have all required permissions
      return item.requiredPermissions.every(permission =>
        userPermissions.includes(permission)
      );
    }

    // No required permissions, so user has access based on role alone
    return true;
  });
};

/**
 * Get navigation item by path
 * @param {string} path - The route path to find
 * @returns {NavigationItem|null} The navigation item or null if not found
 */
export const getNavigationItemByPath = path => {
  return NAVIGATION_CONFIG.find(item => item.path === path) || null;
};

/**
 * Get navigation item by ID
 * @param {string} id - The navigation item ID
 * @returns {NavigationItem|null} The navigation item or null if not found
 */
export const getNavigationItemById = id => {
  return NAVIGATION_CONFIG.find(item => item.id === id) || null;
};

/**
 * Check if a user has access to a specific navigation item
 * @param {NavigationItem} navigationItem - The navigation item to check
 * @param {string} userRole - The user's role
 * @returns {boolean} True if user has access, false otherwise
 */
export const hasAccessToNavigationItem = (navigationItem, userRole) => {
  if (!navigationItem || !userRole) {
    return false;
  }

  return navigationItem.roles.includes(userRole);
};

/**
 * Get the default navigation path for a user role
 * @param {string} userRole - The user's role
 * @returns {string} The default path for the role
 */
export const getDefaultPathForRole = userRole => {
  // Special handling for COMPANY_ADMIN role
  if (userRole === "COMPANY_ADMIN") {
    const companyItem = NAVIGATION_CONFIG.find(item => item.id === "company");
    if (companyItem && companyItem.roles.includes(userRole)) {
      return companyItem.path;
    }
  }

  // Special handling for REPPI_ADMIN role
  if (userRole === "REPPI_ADMIN") {
    const reppiAdminItem = NAVIGATION_CONFIG.find(
      item => item.id === "reppi-admin"
    );
    if (reppiAdminItem && reppiAdminItem.roles.includes(userRole)) {
      return reppiAdminItem.path;
    }
  }

  // Look for items marked as default for the role
  const defaultItem = NAVIGATION_CONFIG.find(
    item => item.isDefault && item.roles.includes(userRole)
  );

  if (defaultItem) {
    return defaultItem.path;
  }

  // Fallback to first available item for the role
  const firstAvailableItem = NAVIGATION_CONFIG.find(item =>
    item.roles.includes(userRole)
  );

  return firstAvailableItem ? firstAvailableItem.path : "/dashboard";
};

/**
 * Helper function to detect conflicting paths that shouldn't match
 * @param {string} itemPath - The navigation item path
 * @param {string} currentPath - The current path
 * @returns {boolean} True if paths conflict and shouldn't match
 */
const isConflictingPath = (itemPath, currentPath) => {
  // Define paths that should not match each other
  const conflicts = [["/training", "/training-courses"]];

  for (const [path1, path2] of conflicts) {
    if (
      (itemPath === path1 && currentPath.startsWith(path2)) ||
      (itemPath === path2 && currentPath.startsWith(path1))
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Check if a path is active based on current location
 * This handles exact matches and nested routes
 * @param {string} itemPath - The navigation item path
 * @param {string} currentPath - The current location pathname
 * @returns {boolean} True if the path should be considered active
 */
export const isPathActive = (itemPath, currentPath) => {
  if (!itemPath || !currentPath) {
    return false;
  }

  // Handle query parameters and hash fragments first
  // Extract the pathname without query params or hash
  const currentPathOnly = currentPath.split("?")[0].split("#")[0];

  // Normalize paths by removing trailing slashes
  const normalizedItemPath = itemPath.replace(/\/$/, "") || "/";
  const normalizedCurrentPath = currentPathOnly.replace(/\/$/, "") || "/";

  // Exact match
  if (normalizedItemPath === normalizedCurrentPath) {
    return true;
  }

  // Handle dynamic routes (e.g., /reps/123 should activate /reps)
  // Only match if the current path starts with the item path followed by a slash
  // and the item path is not the root path
  // BUT exclude cases where we have similar paths like /training and /training-courses
  if (
    normalizedItemPath !== "/" &&
    normalizedCurrentPath.startsWith(normalizedItemPath + "/") &&
    !isConflictingPath(normalizedItemPath, normalizedCurrentPath)
  ) {
    return true;
  }

  // Special handling for nested routes and related pages
  const routeMatches = {
    // Training-related routes (but NOT training-courses)
    "/training": ["/rep-training"],
    // Company-related routes
    "/company": ["/company-assignment"],
    // Dashboard/Home routes
    "/dashboard": ["/account", "/verify-email"],
  };

  // Check if current path is a nested route of the item path
  if (routeMatches[normalizedItemPath]) {
    return routeMatches[normalizedItemPath].includes(normalizedCurrentPath);
  }

  // Special handling for Training-related dynamic routes
  // Keep Training nav item highlighted when viewing video routes
  if (normalizedItemPath === "/training") {
    if (normalizedCurrentPath.startsWith("/video/")) {
      return true;
    }
  }

  return false;
};

/**
 * Validate navigation configuration
 * Ensures all required fields are present and valid
 * @returns {boolean} True if configuration is valid
 */
export const validateNavigationConfig = () => {
  const requiredFields = ["id", "label", "path", "icon", "roles"];

  for (const item of NAVIGATION_CONFIG) {
    // Check required fields
    for (const field of requiredFields) {
      if (!item[field]) {
        // Navigation item missing required field
        return false;
      }
    }

    // Check roles array
    if (!Array.isArray(item.roles) || item.roles.length === 0) {
      // Navigation item must have at least one role
      return false;
    }

    // Check path format
    if (!item.path.startsWith("/")) {
      // Navigation item path must start with "/"
      return false;
    }
  }

  // Check for duplicate IDs
  const ids = NAVIGATION_CONFIG.map(item => item.id);
  const uniqueIds = new Set(ids);
  if (ids.length !== uniqueIds.size) {
    // Duplicate navigation item IDs found
    return false;
  }

  // Check for duplicate paths
  const paths = NAVIGATION_CONFIG.map(item => item.path);
  const uniquePaths = new Set(paths);
  if (paths.length !== uniquePaths.size) {
    // Duplicate navigation item paths found
    return false;
  }

  return true;
};

/**
 * Get all available paths from navigation configuration
 * @returns {string[]} Array of all navigation paths
 */
export const getAllNavigationPaths = () => {
  return NAVIGATION_CONFIG.map(item => item.path);
};

/**
 * Get navigation breadcrumb for a given path
 * @param {string} path - The current path
 * @returns {NavigationItem[]} Array of navigation items forming breadcrumb
 */
export const getNavigationBreadcrumb = path => {
  const currentItem = getNavigationItemByPath(path);
  if (!currentItem) {
    return [];
  }

  // For now, return just the current item
  // This can be extended to support hierarchical navigation
  return [currentItem];
};

/**
 * Check if navigation should update based on route change
 * @param {string} previousPath - Previous route path
 * @param {string} currentPath - Current route path
 * @returns {boolean} True if navigation state should update
 */
export const shouldUpdateNavigation = (previousPath, currentPath) => {
  if (!previousPath || !currentPath) {
    return true;
  }

  // Update if the active navigation item changes
  const previousItem = NAVIGATION_CONFIG.find(item =>
    isPathActive(item.path, previousPath)
  );
  const currentItem = NAVIGATION_CONFIG.find(item =>
    isPathActive(item.path, currentPath)
  );

  return previousItem?.id !== currentItem?.id;
};

/**
 * Debug function to get active navigation items for a given path
 * Useful for debugging active state issues
 * @param {string} path - The path to check
 * @returns {NavigationItem[]} Array of active navigation items
 */
export const getActiveNavigationItems = path => {
  return NAVIGATION_CONFIG.filter(item => isPathActive(item.path, path));
};

/**
 * Debug function to test path matching
 * @param {string} itemPath - Navigation item path
 * @param {string} currentPath - Current path to test
 * @returns {Object} Debug information about the match
 */
export const debugPathMatch = (itemPath, currentPath) => {
  const isActive = isPathActive(itemPath, currentPath);
  const currentPathOnly = currentPath.split("?")[0].split("#")[0];
  const normalizedItemPath = itemPath.replace(/\/$/, "") || "/";
  const normalizedCurrentPath = currentPathOnly.replace(/\/$/, "") || "/";

  return {
    itemPath,
    currentPath,
    currentPathOnly,
    normalizedItemPath,
    normalizedCurrentPath,
    isActive,
    exactMatch: normalizedItemPath === normalizedCurrentPath,
    startsWithMatch: normalizedCurrentPath.startsWith(normalizedItemPath + "/"),
    hasQueryParams: currentPath.includes("?"),
    hasHashFragment: currentPath.includes("#"),
  };
};

// Validate configuration on module load (development only)
if (process.env.NODE_ENV === "development") {
  if (!validateNavigationConfig()) {
    // Navigation configuration validation failed
  }
}
